import type { ReactNode } from 'react';

export type Definition = {
  emoji: string;
  term: string;
  definition: string;
  url: string;
};

export type GoodToKnowArticle = {
  icon: string;
  title: string;
  synopsis: string;
  url: string;
  readTime: string;
};

export const DEFINITIONS: Definition[] = [
  {
    emoji: '🛡️',
    term: 'Fiduciary',
    definition:
      "A fiduciary is an individual or organization that has a legal duty to act in the best financial interests of someone else, placing the client’s financial welfare above their own. Ask any prospective service provider if they're a fiduciary, and verify their regulatory and professional status. Fiduciary duties depend on the profession and any regulations surrounding the role. For example, board members may have certain fiduciary duties to the companies that they advise. Trustees owe fiduciary duties when they manage assets for their  beneficiaries.",
    url: 'https://www.nerdwallet.com/article/investing/fiduciary',
  },
  {
    emoji: '💰',
    term: 'Fee-Only vs. Fee-Based',
    definition:
      'Fee-only advisors are compensated solely by client fees. A fee-based financial planner is paid by the client but may also receive commissions from other companies for selling certain financial products. This can create a conflict of interest, because a fee-based advisor may charge you for advice while potentially steering you toward investment products that personally benefit them. Choosing a fee-only financial planner who follows the fiduciary standard is usually a better choice for most investors. Several professional groups require members to abide by the fiduciary standard, including The National Association of Personal Financial Advisors, Garrett Planning Network, XY Planning Network and the Alliance of Comprehensive Planners.',
    url: 'https://www.nerdwallet.com/article/investing/financial-advisor-fees',
  },
  {
    emoji: '📋',
    term: 'Form ADV',
    definition:
      'Form ADV (“the Uniform Application for Investment Advisor Registration”) is one of the most important documents to check if you’re going to hire an investment adviser (or already have one). This free disclosure document reveals everything from information about an advisor’s fee structure and firm history to their management style and any misconduct. If an investment advisor receives compensation for recommending securities products, you’ll see the relationship disclosed in Form ADV. If they are paid based on a percentage of assets managed or a flat-rate for one-off services, that will be spelled out.  ',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/form-adv',
  },
  {
    emoji: '📊',
    term: 'Assets Under Management (AUM)',
    definition:
      "AUM represents the total market value of investments an advisor manages. It's a key indicator of scale and experience level.",
    url: 'https://www.nerdwallet.com/article/investing/financial-advisor-fees',
  },
  {
    emoji: '🏛️',
    term: 'SEC vs. State Registration',
    definition:
      'Advisors managing $100M+ typically register with the SEC. Smaller firms register with their state. Both must file Form ADV.',
    url: 'https://adviserinfo.sec.gov',
  },
  {
    emoji: '🔄',
    term: 'Wrap Fees',
    definition:
      'A wrap fee bundles advisory, brokerage, and admin costs into one charge. It simplifies billing but can be more expensive for buy-and-hold investors.',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/wrap-fees',
  },
  {
    emoji: '⚠️',
    term: 'Disclosures',
    definition:
      'Item 11 of Form ADV covers disciplinary history including regulatory actions, civil proceedings, and complaints. Always review these on the SEC IAPD site.',
    url: 'https://adviserinfo.sec.gov',
  },
];

export const GOOD_TO_KNOW: GoodToKnowArticle[] = [
  {
    icon: '💡',
    title: 'What Does a Financial Advisor Actually Do?',
    synopsis:
      'A "Financial advisor" is a catch-all term that describes a wide variety of financial service providers, including investment advisors, investment managers, broker-dealers and financial planners. All of these  can help you make investment decisions. Services a financial adviser might provide include building investment plans, recommending investments, managing investment portfolios, assessing risk, providing financial guidance, and monitoring and rebalancing investment accounts. ',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/what-does-a-financial-advisor-do',
    readTime: '9 min',
  },
  {
    icon: '🔍',
    title: 'How to Choose the Right Financial Advisor',
    synopsis:
      'To select a financial advisor, you generally want to identify what you need in an advisor and how much you can afford to pay, then shop around. Compare advisors by checking their Form ADVs, verifying their fiduciary status and understanding how they are paid and exactly what services you’ll receive in exchange. ',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/how-to-choose-a-financial-advisor',
    readTime: '10 min',
  },
  {
    icon: '❓',
    title: '10 Questions to Ask Before You Hire an Advisor',
    synopsis:
      'Ask about fiduciary status, compensation, investment philosophy, typical client profile, and what happens if you want to end the relationship. ',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/10-questions-ask-financial-advisor',
    readTime: '7 min',
  },
  {
    icon: '💲',
    title: 'How Much Does an Advisor Cost?',
    synopsis:
      'Most charge ~1% of AUM annually. Some charge flat fees ($2,500 to $9,200), hourly rates ($200 to $400), or monthly subscriptions. Often, fees get lower if you have higher amounts under management.',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/how-much-does-a-financial-advisor-cost',
    readTime: '9 min',
  },
  {
    icon: '⏰',
    title: 'When Do You Need an Advisor?',
    synopsis:
      'Major life changes — new job, marriage, inheritance, approaching retirement — are common triggers. Complex finances signal it is time. But financial advisors can also help you get off on the right financial foot from the start.',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/do-i-need-financial-advisor',
    readTime: '5 min',
  },
  {
    icon: '🤝',
    title: 'What to Expect in Your First Meeting',
    synopsis:
      'An overview of what an initial consultation with an advisor will entail, what you should bring and key questions to ask. Plus, a few red flags you might want to watch for. ',
    url: 'https://www.nerdwallet.com/financial-advisors/learn/financial-advisor-what-to-expect',
    readTime: '5 min',
  },
];

export const CHECKLIST_ITEMS: ReactNode[] = [
  <>
    Verify the advisor is registered on the{' '}
    <a href="https://adviserinfo.sec.gov/" target="_blank" rel="noopener noreferrer">
      SEC's IAPD website
    </a>
  </>,
  'Confirm they have a fiduciary duty to act in your best interest',
  'Review their Form ADV Part 2 for fees, conflicts, and disciplinary history',
  'Ask how they are compensated (fee-only is generally least conflicted)',
  'Check credentials - look for CFP®, CFA, or CPA designations',
  'Ask about their typical client profile and minimum account requirements',
  'Understand what services are included and what costs extra',
  'Get everything in writing before signing an advisory agreement',
];
